import { err, ok, type Result } from "neverthrow";

import { buildInvalidError } from "@thalamos/common";

import type {
  CreateIncidentMinimumFields,
  DetentionDetailsMinimumFields,
  FinishFormMinimumFields,
  InformationForHealthcareMinimumFields,
  McaAccompanimentMinimumFields,
  McaCapacityAssessmentMinimumFields,
  McaIncidentDetailsMinimumFields,
  McaPersonDetailsMinimumFields,
  PersonDetailsMinimumFields,
  RecordPreEndIncidentMinimumFields,
  TriageIncidentMinimumFields,
  WorkflowPdfInputData,
} from "../../types/workflow/index.js";
import {
  WorkflowVersionSchema,
  type WorkflowVersion,
} from "../../types/workflowVersion.js";
import type { PdfInputData_BTP_1_0 } from "../workflow_BTP_1_0/types/pdf.js";
import type { PdfInputData_BTP_1_1 } from "../workflow_BTP_1_1/types/pdf.js";

/**
 * Grouped type parameters for S136 form sections.
 * Reduces the number of individual generic parameters needed.
 */
export type S136SectionTypes<
  TCreateIncident extends CreateIncidentMinimumFields,
  TTriage extends TriageIncidentMinimumFields,
  TPersonDetails extends Partial<PersonDetailsMinimumFields>,
  TDetentionDetails extends Partial<DetentionDetailsMinimumFields>,
  TInformationForHealthcare extends
    Partial<InformationForHealthcareMinimumFields>,
  TFinishForm extends Partial<FinishFormMinimumFields>,
  TPreEndIncident extends Partial<RecordPreEndIncidentMinimumFields>,
> = {
  createIncident: TCreateIncident;
  triage: TTriage;
  personDetails: TPersonDetails;
  detentionDetails: TDetentionDetails;
  informationForHealthcare: TInformationForHealthcare;
  finishForm: TFinishForm;
  preEndIncident: TPreEndIncident;
};

/**
 * Grouped type parameters for MCA form sections.
 * Reduces the number of individual generic parameters needed.
 */
export type McaSectionTypes<
  TPersonDetails extends Partial<McaPersonDetailsMinimumFields>,
  TIncidentDetails extends Partial<McaIncidentDetailsMinimumFields>,
  TCapacityAssessment extends Partial<McaCapacityAssessmentMinimumFields>,
  TAccompaniment extends Partial<McaAccompanimentMinimumFields>,
> = {
  personDetails: TPersonDetails;
  incidentDetails: TIncidentDetails;
  capacityAssessment: TCapacityAssessment;
  accompaniment: TAccompaniment;
};

/**
 * Helper type to extract WorkflowPdfInputData from grouped section types.
 */
type WorkflowPdfInputDataFromSections<
  TS136 extends S136SectionTypes<
    CreateIncidentMinimumFields,
    TriageIncidentMinimumFields,
    Partial<PersonDetailsMinimumFields>,
    Partial<DetentionDetailsMinimumFields>,
    Partial<InformationForHealthcareMinimumFields>,
    Partial<FinishFormMinimumFields>,
    Partial<RecordPreEndIncidentMinimumFields>
  >,
  TMca extends McaSectionTypes<
    Partial<McaPersonDetailsMinimumFields>,
    Partial<McaIncidentDetailsMinimumFields>,
    Partial<McaCapacityAssessmentMinimumFields>,
    Partial<McaAccompanimentMinimumFields>
  >,
> = WorkflowPdfInputData<
  TS136["createIncident"],
  TS136["triage"],
  TS136["personDetails"],
  TS136["detentionDetails"],
  TS136["informationForHealthcare"],
  TS136["finishForm"],
  TS136["preEndIncident"],
  TMca["incidentDetails"],
  TMca["capacityAssessment"],
  TMca["personDetails"],
  TMca["accompaniment"]
>;

// Transformer function for BTP_1_0 to BTP_1_1
export function transformBTP1_0To1_1(
  data: PdfInputData_BTP_1_0,
): Result<PdfInputData_BTP_1_1, never> {
  // Transform the preEndIncident data, excluding BTP_1_0-specific fields
  const transformedPreEndIncident: PdfInputData_BTP_1_1["preEndIncident"] =
    data.preEndIncident
      ? {
          ...data.preEndIncident,
          dateTimeOfDetention: undefined,
          didNotArrive: undefined,
          arrivalDateTime: undefined,
          wereAskedToRemainAtPosByHealthAmhp: undefined,
          remainedLongerAtPosDueToHealthAmhpRequest: undefined,
        }
      : undefined;

  // Transform the finish data
  const transformedFinish: PdfInputData_BTP_1_1["finish"] = data.finish
    ? {
        ...data.finish,
        dateTimeLeft: data.finish.dateTimeLeft ?? undefined,
        whatWasTheOutcome: data.finish.whatWasTheOutcome ?? undefined,
      }
    : undefined;

  // Construct the transformed PdfInputData_BTP_1_1
  const transformedData: PdfInputData_BTP_1_1 = {
    ...data,
    incidentWorkItem: {
      ...data.incidentWorkItem,
      incidentWorkItem: {
        ...data.incidentWorkItem.incidentWorkItem,
        workflowVersion: WorkflowVersionSchema.enum.BTP_1_1,
      },
    },
    preEndIncident: transformedPreEndIncident,
    finish: transformedFinish,
  };

  return ok(transformedData);
}

/**
 * btp-1.2 stores the same fields as btp-1.1 and differs only in what it
 * requires, so a record carried forward changes only its version.
 */
export function transformBTP1_1To1_2(
  data: PdfInputData_BTP_1_1,
): Result<PdfInputData_BTP_1_1, never> {
  return ok({
    ...data,
    incidentWorkItem: {
      ...data.incidentWorkItem,
      incidentWorkItem: {
        ...data.incidentWorkItem.incidentWorkItem,
        workflowVersion: WorkflowVersionSchema.enum.BTP_1_2,
      },
    },
  });
}

// Transformer registry for PdfInputData
export const transformerRegistry: Record<
  WorkflowVersion,
  (
    // prettier-ignore
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: WorkflowPdfInputData<any, any, any, any, any, any, any, any, any, any, any>,
  ) => Result<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    WorkflowPdfInputData<any, any, any, any, any, any, any, any, any, any, any>,
    unknown
  >
> = {
  [WorkflowVersionSchema.enum.BTP_1_0]: transformBTP1_0To1_1,
  [WorkflowVersionSchema.enum.BTP_1_1]: transformBTP1_1To1_2,
  [WorkflowVersionSchema.enum.BTP_1_2]: (_data) =>
    err(buildInvalidError({ message: "Invalid workflow version transition" })),
  [WorkflowVersionSchema.enum.MET_1_0]: (_data) =>
    err(buildInvalidError({ message: "Invalid workflow version transition" })),
  [WorkflowVersionSchema.enum.COLP_1_0]: (_data) =>
    err(buildInvalidError({ message: "Invalid workflow version transition" })),
};

/**
 * Transforms workflow PDF input data from one workflow version to another.
 *
 * Uses grouped type parameters to reduce complexity:
 * - S136In/S136Out: Section 136 form sections (7 types each)
 * - McaIn/McaOut: Mental Capacity Act form sections (3 types each)
 */
export function transform<
  // Input S136 sections
  TS136In extends S136SectionTypes<
    CreateIncidentMinimumFields,
    TriageIncidentMinimumFields,
    Partial<PersonDetailsMinimumFields>,
    Partial<DetentionDetailsMinimumFields>,
    Partial<InformationForHealthcareMinimumFields>,
    Partial<FinishFormMinimumFields>,
    Partial<RecordPreEndIncidentMinimumFields>
  >,
  // Input MCA sections
  TMcaIn extends McaSectionTypes<
    Partial<McaPersonDetailsMinimumFields>,
    Partial<McaIncidentDetailsMinimumFields>,
    Partial<McaCapacityAssessmentMinimumFields>,
    Partial<McaAccompanimentMinimumFields>
  >,
  // Output S136 sections
  TS136Out extends S136SectionTypes<
    CreateIncidentMinimumFields,
    TriageIncidentMinimumFields,
    Partial<PersonDetailsMinimumFields>,
    Partial<DetentionDetailsMinimumFields>,
    Partial<InformationForHealthcareMinimumFields>,
    Partial<FinishFormMinimumFields>,
    Partial<RecordPreEndIncidentMinimumFields>
  >,
  // Output MCA sections
  TMcaOut extends McaSectionTypes<
    Partial<McaPersonDetailsMinimumFields>,
    Partial<McaIncidentDetailsMinimumFields>,
    Partial<McaCapacityAssessmentMinimumFields>,
    Partial<McaAccompanimentMinimumFields>
  >,
>(
  data: WorkflowPdfInputDataFromSections<TS136In, TMcaIn>,
  targetWorkflowVersion: WorkflowVersion,
): Result<WorkflowPdfInputDataFromSections<TS136Out, TMcaOut>, unknown> {
  const currentWorkflowVersion = data.incidentWorkItem.incidentWorkItem
    .workflowVersion as WorkflowVersion;

  if (currentWorkflowVersion === targetWorkflowVersion) {
    return ok(
      data as unknown as WorkflowPdfInputDataFromSections<TS136Out, TMcaOut>,
    );
  }

  const transformer = transformerRegistry[currentWorkflowVersion];

  const result = transformer(data);
  if (result.isErr()) {
    return result;
  }

  return transform(result.value, targetWorkflowVersion);
}
